import { schema } from 'normalizr';
import type * as Hub from '~/ogm-hub';
import type { UmSelectionMode } from '~/domain';

export enum LoadingStatus {
	idle = 'idle',
	loading = 'loading',
	success = 'success',
	failure = 'failure',
}

export enum EligibilityStatus {
	None = 0,
	Some = 1,
	All = 2,
}

export enum ProductViewTab {
	selection = 'Select Products',
	coverage = 'Distribution Centers & Coverage',
}

export enum OGMAIViewTab {
	optimizationDashboard = 'Optimization Dashboard',
	suggestedOptimization = 'Suggested Optimization',
	grouping = 'Grouping',
	history = 'History',
	suggestedGrouping = 'Suggested Grouping',
}

export enum GridViewDisplayType {
	price = 'price',
	coverage = 'coverage',
	pricePerUnit = 'pricePerUnit',
}

export interface IProductHeader {
	id: string;
	corpSupplierCode: string;
	supplierProductNumber: string;
	unitMeasureCodes: string[];

	description: string;
	extendedDescription: string;
	brand: string;
	packSize: string;
	conversionQuantity: number;

	hasBrandFlag: boolean;
	hasDemand: boolean;
	hasRemote: boolean;
	hasNonStock: boolean;
	hasPermutations: boolean;
	hasPhaseOutReplacement: boolean;

	nonstockDcs: Hub.DistributionCenterWithUMs[];
	remoteStockDcs: Hub.DistributionCenterWithUMs[];
	demandStockDcs: Hub.DistributionCenterWithUMs[];
	productAlertDcs: Hub.DistributionCenterWithUMs[];
	propDcs: Hub.DistributionCenterWithUMs[];
	proprietary?: number;

	supplierCategoryLevel1Code: string;
	supplierCategoryLevel2Code: string;
	supplierCategoryLevel3Code: string;
	supplierCategoryLevel4Code: string;

	supplierCategoryLevel1Description: string;
	supplierCategoryLevel2Description: string;
	supplierCategoryLevel3Description: string;
	supplierCategoryLevel4Description: string;

	activeDistributionCenters?: Hub.DistributionCenterWithUMs[];
	coveredDistributionCenters?: Hub.DistributionCenterWithUMs[];
	glGroup?: Hub.GlGroup;

	specIndicatorId?: number;

	isSupplierRecommendation?: boolean;

	productNetUnits: Hub.ProductNetUnits[];
}

export interface IProductLine {
	id: string;
	corpSupplierCode: string;
	distributionCenterDssiCode: string;
	supplierProductNumber: string;
	unitMeasureCodes: string[];

	distributionCenterName: string;
	status: string;
	manufacturerProductNumber: string;
	isProprietary: boolean;
	isEligible: boolean;
	eligibilityStatus?: EligibilityStatus;
	isDemand: boolean;
	isRemote: boolean;
	isNonstock: boolean;
	isBranded: boolean;
	distributionCenterDisplayCode: string;
	phaseOutReplacementProduct: string;
}

export interface IDistributionCenter {
	id: number;
	name: string;
	externalCode: string;
	dssiCode: string;
	supplierId: number;
}

export type IProductCoverage = Record<
	string,
	{
		additionalCoverageCount: number;
		coverageFromThisProduct: number;
		coveredDCs: string[];
		totalDcCount: number;
		specIndicatorId?: number;
	}
>;

export interface IProductAvailability {
	blueprintId: number;
	locationCode: string;
	supplierProductNumber: string;
	isAvailable: boolean;
}

export function productHeaderId(productHeader: IProductHeader) {
	return [
		productHeader.corpSupplierCode,
		productHeader.supplierProductNumber,
	].join('|');
}

export function productLineId(productLine: IProductLine) {
	return [
		productLine.corpSupplierCode,
		productLine.distributionCenterDssiCode,
		productLine.supplierProductNumber,
	].join('|');
}

export function productAvailabilityId(product: IProductAvailability) {
	return [
		product.blueprintId,
		product.locationCode,
		product.supplierProductNumber,
	].join('|');
}

export const productHeaderEntity = new schema.Entity(
	'productHeaders',
	{},
	{
		idAttribute: productHeaderId,
		processStrategy: ({
			categoryLevel1Code,
			categoryLevel1,
			categoryLevel2Code,
			categoryLevel2,
			categoryLevel3Code,
			categoryLevel3,
			categoryLevel4Code,
			categoryLevel4,
			...product
		}) => ({
			...product,
			supplierCategoryLevel1Code: categoryLevel1Code,
			supplierCategoryLevel2Code: categoryLevel2Code,
			supplierCategoryLevel3Code: categoryLevel3Code,
			supplierCategoryLevel4Code: categoryLevel4Code,
			supplierCategoryLevel1Description: categoryLevel1,
			supplierCategoryLevel2Description: categoryLevel2,
			supplierCategoryLevel3Description: categoryLevel3,
			supplierCategoryLevel4Description: categoryLevel4,
		}),
	}
);

export const productLineEntity = new schema.Entity(
	'productLines',
	{},
	{ idAttribute: productLineId }
);

export interface IProduct {
	corpSupplierCode: string;
	supplierProductNumber: string;
	unitMeasureCode: string;
	specIndicatorId?: number;
}

export interface IProductWithNetUnits {
	corpSupplierCode: string;
	supplierProductNumber: string;
	unitMeasureCode: string;
	specIndicatorId?: number;
	productNetUnits: Hub.ProductNetUnits[];
}

export interface IMetaProduct {
	id?: number;
	name: string;
	lastSearchTerm: string;
	locationsWithCoverageCount: number;
	distributionCentersWithCoverageCount: number;
	distributionCenterPriorityOverrides: IMetaProductDistributionCenterOverride[];
	isSelectByUm: boolean;
	umSelectionModeId: UmSelectionMode;
	productHeaders: string[];
	products: IProduct[];
	selectionRuleSetId: number;
	pendingRebuild: boolean;
	maintainedBy: IMaintainedByType;
	statusId: IMetaProductStatus;
	specIndicatorId?: number;
	customCategoryId?: number;
	productSpecIndicatorIds?: IProductSpecIndicatorsDict;
	choices: Hub.GetMetaProductChoicesGivenProductsAndRuleSetResponse;
	MetaProductType: Hub.MetaProductType;
	tagLinks: Hub.MetaProductTagLink[];
	isOnMenu: boolean;
}

export const AvailableMaintainedByTypes = [1, 3, 4, 2];

export enum IMaintainedByType {
	OGM3 = 1,
	OGMAIAuto = 2,
	OGMAIManual = 3,
	OGMAIAutoReprioritize = 4,
}

export const AvailableSpecIndicatorTypes = [0, 1, 2, 3];

export enum ISpecIndicatorType {
	Unassigned = 0,
	Hard = 1,
	Required = 2,
	Open = 3,
}

export enum IMetaProductStatus {
	Active = 1,
	Draft = 2,
}

export interface IMetaProductDistributionCenterOverride {
	distributionCenterId: number;
	supplierProductNumber: string;
	unitMeasureCode: string;
	overrideType: IMetaProductDistributionCenterOverrideType;
}

export enum IMetaProductDistributionCenterOverrideType {
	Boost = 1,
	Exclude = 2,
}

export interface IRestrictionList {
	id: number;
	name: string;
}

export type IProductSpecIndicatorsDict = Record<string, number>;

export const metaProductEntity = new schema.Entity(
	'metaProducts',
	{ productHeaders: [productHeaderEntity] },
	{
		processStrategy: (metaProduct) => {
			const productSpecIndicatorIds: IProductSpecIndicatorsDict = {};
			const isSelectByUm = metaProduct.isSelectByUm;

			const products: IProduct[] = [];
			metaProduct.products.forEach((product) => {
				products.push({
					corpSupplierCode: product.corpSupplierCode,
					supplierProductNumber: product.supplierProductNumber,
					unitMeasureCode: product.unitMeasureCode,
					specIndicatorId: product.specIndicatorId,
				});

				const metaProductItemId = isSelectByUm
					? `${product.corpSupplierCode}|${product.supplierProductNumber}|${product.unitMeasureCode}`
					: `${product.corpSupplierCode}|${product.supplierProductNumber}`;
				productSpecIndicatorIds[metaProductItemId] =
					product.specIndicatorId || 0;
			});

			return {
				id: metaProduct.id,
				name: metaProduct.name,
				lastSearchTerm: metaProduct.search,
				locationsWithCoverageCount: metaProduct.locationAvailability,
				distributionCentersWithCoverageCount:
					metaProduct.distributionCenterAvailability,
				productHeaders: metaProduct.productHeaders,
				products,
				distributionCenterPriorityOverrides:
					metaProduct.distributionCenterOverrides,
				isSelectByUm: metaProduct.isSelectByUm,
				umSelectionModeId: metaProduct.umSelectionModeId,
				maintainedBy: metaProduct.maintainedBy,
				statusId: metaProduct.statusId,
				selectionRuleSetId: metaProduct.selectionRuleSetId,
				pendingRebuild: metaProduct.pendingRebuild,
				specIndicatorId: metaProduct.specIndicatorId,
				customCategoryId: metaProduct.customCategoryId,
				productSpecIndicatorIds,
				MetaProductType: metaProduct.metaProductType,
				tagLinks: metaProduct.tagLinks || [],
				isOnMenu: metaProduct.isOnMenu,
			};
		},
	}
);

export interface IMetaProductNormalizeResult {
	metaProducts: Record<string, IMetaProduct>;
	productHeaders: Record<string, IProductHeader>;
}

export interface IProductLinesNormalizeResult {
	productLines: Record<string, IProductLine>;
}
